import { useState, useMemo } from "react";
import { num, toM, ml, m2, money, ceilStep, minSale, UNITS, CalcLayout, FormCol, Card, Field, Sel, HowTo, Advanced, AreaTable, ResultCard, useCatalog, ProductSelect } from "./shared";

// Franjas desde rollo de ancho fijo, optimizando orientación
function franjas(spaces, u, rolloAnchoM, direccional) {
  let mlA = 0, mlB = 0, uniones = 0;
  spaces.forEach((sp) => {
    const cant = Math.max(1, Math.round(num(sp.cant)) || 1);
    const L = toM(sp.largo, u), W = toM(sp.ancho, u);
    if (L <= 0 || W <= 0) return;
    const fA = Math.ceil(W / rolloAnchoM), fB = Math.ceil(L / rolloAnchoM);
    mlA += fA * L * cant; mlB += fB * W * cant;
    uniones += Math.max(0, fA - 1) * L * cant; // se recalcula abajo según elección
  });
  const useA = direccional || mlA <= mlB;
  let ml = 0, uni = 0;
  spaces.forEach((sp) => {
    const cant = Math.max(1, Math.round(num(sp.cant)) || 1);
    const L = toM(sp.largo, u), W = toM(sp.ancho, u);
    if (L <= 0 || W <= 0) return;
    const f = useA ? Math.ceil(W / rolloAnchoM) : Math.ceil(L / rolloAnchoM);
    const len = useA ? L : W;
    ml += f * len * cant; uni += Math.max(0, f - 1) * len * cant;
  });
  return { ml, uniones: uni };
}

export default function Alfombras({ onSave, s }) {
  const items = useCatalog("alfombras");
  const [pid, setPid] = useState("");
  const prod = items.find((p) => p.id === pid);
  const [u, setU] = useState("m");
  const [tipo, setTipo] = useState("Residencial en rollo");
  const [spaces, setSpaces] = useState([{ nombre: "Área 1", largo: "5", ancho: "4", cant: "1" }]);
  const [rolloAncho, setRolloAncho] = useState("3.66");
  const [direccional, setDireccional] = useState("no");
  const [desp, setDesp] = useState("10");
  const [ventaMin, setVentaMin] = useState("1");
  const [bajoAlf, setBajoAlf] = useState("si");
  const [adhRend, setAdhRend] = useState("4");
  const [precioM2, setPrecioM2] = useState("");
  const [precioInst, setPrecioInst] = useState("");
  const pr = prod ? prod.price : num(precioM2);

  const r = useMemo(() => {
    const rolloAnchoM = num(rolloAncho) || 3.66;
    const { ml: mlTeorico, uniones } = franjas(spaces, u, rolloAnchoM, direccional === "si");
    const areaReal = spaces.reduce((a, sp) => a + toM(sp.largo, u) * toM(sp.ancho, u) * Math.max(1, Math.round(num(sp.cant)) || 1), 0);
    const mlAjust = mlTeorico * (1 + num(desp) / 100);
    const mlVenta = ceilStep(minSale(mlAjust, num(ventaMin)), 0.5 < 1 ? 0 : 0) || Math.ceil(mlAjust);
    const mlFinal = Math.max(Math.ceil(mlAjust), num(ventaMin));
    const areaComprada = mlFinal * rolloAnchoM;
    const aprov = areaComprada > 0 ? (areaReal / areaComprada) * 100 : 0;
    const bajo = bajoAlf === "si" ? areaReal : 0;
    const adhesivo = num(adhRend) > 0 ? Math.ceil(areaReal / num(adhRend)) : 0;
    const cintaUnion = uniones * 1.05;
    const perim = spaces.reduce((a, sp) => a + 2 * (toM(sp.largo, u) + toM(sp.ancho, u)) * Math.max(1, Math.round(num(sp.cant)) || 1), 0);
    const cMat = mlFinal * rolloAnchoM * pr;
    const total = cMat + num(precioInst);
    const w = [];
    if (areaReal === 0) w.push("Captura al menos un área con medidas.");
    if (aprov < 70 && areaReal > 0) w.push(`Aprovechamiento bajo (${aprov.toFixed(0)}%): el ancho fijo del rollo genera merma. Considera otro ancho.`);
    if (direccional === "si") w.push("Alfombra direccional: no se optimiza por rotación de franjas.");
    if (prod && prod.stock < areaComprada) w.push(`Inventario insuficiente de "${prod.name}": stock ${prod.stock} m², requieres ${areaComprada.toFixed(2)} m².`);
    const rows = [
      { section: "Proyecto" },
      { label: "Tipo de alfombra", value: tipo }, { label: "Número de áreas", value: `${spaces.length}` },
      { label: "Ancho del rollo", value: ml(rolloAnchoM) }, { label: "Área real", value: m2(areaReal) },
      { section: "Cálculo (franjas)" },
      { label: "Metros lineales teóricos", value: ml(mlTeorico) }, { label: "Desperdicio aplicado", value: `${num(desp)}%` },
      { label: "Metros lineales a vender", value: ml(mlFinal), strong: true }, { label: "Área comprada", value: m2(areaComprada) },
      { label: "Aprovechamiento", value: `${aprov.toFixed(0)}%` },
      { section: "Complementos" },
      ...(bajo ? [{ label: "Bajo alfombra", value: m2(bajo) }] : []),
      { label: "Adhesivo (unidades)", value: `${adhesivo}` },
      ...(cintaUnion > 0 ? [{ label: "Cinta de unión", value: ml(cintaUnion) }] : []),
      { label: "Perfiles / transición (perímetro)", value: ml(perim) },
      { section: "Costos" },
      { label: "Materiales", value: money(cMat) }, { label: "Instalación", value: money(num(precioInst)) },
      { label: "Total estimado", value: money(total), strong: true },
    ];
    return { rows, warnings: w, total, inputs: { u, tipo, rolloAncho } };
  }, [u, tipo, spaces, rolloAncho, direccional, desp, ventaMin, bajoAlf, adhRend, pr, precioInst, prod]);

  return (
    <CalcLayout>
      <FormCol>
        <HowTo title="¿Cómo calcular alfombra en rollo?"><p>El rollo tiene ancho fijo; se cubre en franjas. El sistema prueba ambas orientaciones y elige la de menor metraje (salvo alfombra direccional), suma bajo alfombra, adhesivo, cinta de unión y perfiles.</p></HowTo>
        <Card>
          <Sel label="Unidad" value={u} onChange={setU} opts={UNITS} testid="alf-unidad" />
          <Sel label="Tipo de alfombra" value={tipo} onChange={setTipo} opts={["Residencial en rollo", "Comercial en rollo", "Hotelera", "Alto tráfico", "Lana", "Nylon", "Poliéster", "Polipropileno", "Exterior", "Direccional"]} testid="alf-tipo" />
          <Field label="Ancho del rollo (m)" value={rolloAncho} onChange={setRolloAncho} step="0.01" suffix="m" testid="alf-rolloancho" />
          <Sel label="¿Diseño direccional?" value={direccional} onChange={setDireccional} opts={[["no", "No (optimizar rotación)"], ["si", "Sí (dirección fija)"]]} testid="alf-direccional" />
        </Card>
        <ProductSelect items={items} value={pid} onChange={setPid} testid="alf-product" />
        <AreaTable title="Áreas a cubrir (largo × ancho)" rows={spaces} setRows={setSpaces} u={u} defaultName="Área" testid="alf-areas" />
        <Advanced>
          <Field label="Desperdicio (%)" value={desp} onChange={setDesp} step="1" suffix="%" testid="alf-desp" />
          <Field label="Venta mínima (m lineales)" value={ventaMin} onChange={setVentaMin} step="0.5" suffix="m" testid="alf-ventamin" />
          <Sel label="Bajo alfombra" value={bajoAlf} onChange={setBajoAlf} opts={[["si", "Sí"], ["no", "No"]]} testid="alf-bajo" />
          <Field label="Rendimiento adhesivo (m²/unidad)" value={adhRend} onChange={setAdhRend} step="0.5" suffix="m²" testid="alf-adhrend" />
          <Field label="Precio por m² ($)" value={prod ? prod.price : precioM2} onChange={setPrecioM2} suffix="$" testid="alf-precio" />
          <Field label="Instalación ($)" value={precioInst} onChange={setPrecioInst} suffix="$" testid="alf-precio-inst" />
        </Advanced>
      </FormCol>
      <ResultCard title="Cotización de Alfombra — GOÛT" modality="alfombras" rows={r.rows} warnings={r.warnings} total={r.total} inputs={r.inputs} onSave={onSave} />
    </CalcLayout>
  );
}
